import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import useAppLanguage from "hooks/useAppLanguage";
import { addStudy, getStudiesList, getStudyMeta, subscribeChartWidget } from "./chartToolBridge";

const TABS = [
  { id: "all", labelKey: "indTabAll", label: "All" },
  { id: "trend", labelKey: "indTabTrend", label: "Trend" },
  { id: "oscillators", labelKey: "indTabOscillators", label: "Oscillators" },
  { id: "volumes", labelKey: "indTabVolumes", label: "Volumes" },
  { id: "billWilliams", labelKey: "indTabBillWilliams", label: "Bill Williams" },
];

function norm(name) {
  return String(name || "")
    .toLowerCase()
    .replace(/[_/-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

/** Map existing TV studies onto MT5 tabs. Never invents names that are not in the catalog. */
function tabsForStudy(name) {
  const n = norm(name);
  const tabs = [];

  const isWilliams =
    /\balligator\b/.test(n) ||
    /\bfractals?\b/.test(n) ||
    /\bawesome oscillator\b/.test(n) ||
    n === "ao" ||
    /\baccelerator\b/.test(n) ||
    /\bgator\b/.test(n) ||
    /\bmarket facilitation\b/.test(n) ||
    /\bbill williams\b/.test(n);

  const isVolume =
    n === "volume" ||
    /\bvolume profile\b/.test(n) ||
    /\btick volume/.test(n) ||
    /\bon balance volume\b/.test(n) ||
    n === "obv" ||
    /\baccumulation\b/.test(n) ||
    /\baccum dist\b/.test(n) ||
    /\bchaikin money\b/.test(n) ||
    /\bmoney flow\b/.test(n) ||
    /\bease of movement\b/.test(n) ||
    /\bklinger\b/.test(n) ||
    /\bnet volume\b/.test(n) ||
    /\bpositive volume\b/.test(n) ||
    /\bnegative volume\b/.test(n) ||
    /\bvolume oscillator\b/.test(n);

  const isOsc =
    /\brelative strength\b/.test(n) ||
    n === "rsi" ||
    /\bstochastic\b/.test(n) ||
    /\bmacd\b/.test(n) ||
    /\bcommodity channel\b/.test(n) ||
    n === "cci" ||
    /\bmomentum\b/.test(n) ||
    /williams\s*%/.test(n) ||
    /percent range/.test(n) ||
    /\bawesome oscillator\b/.test(n) ||
    /\baccelerator\b/.test(n) ||
    /\baverage true range\b/.test(n) ||
    n === "atr" ||
    /\bdemarker\b/.test(n) ||
    /\bbears? power\b/.test(n) ||
    /\bbulls? power\b/.test(n) ||
    /\brelative vigor\b/.test(n) ||
    /\brate of change\b/.test(n) ||
    n === "roc" ||
    /\bforce index\b/.test(n) ||
    /\bultimate oscillator\b/.test(n) ||
    /\btrue strength\b/.test(n) ||
    /\bfisher\b/.test(n) ||
    /\bchande\b/.test(n) ||
    /\bdetrended price\b/.test(n) ||
    /\bknow sure thing\b/.test(n) ||
    /\bbalance of power\b/.test(n) ||
    /\bchaikin oscillator\b/.test(n) ||
    /\bconnors\b/.test(n) ||
    /\bosillator\b/.test(n);

  const isTrend =
    !isWilliams &&
    (/\bbollinger\b/.test(n) ||
      /\benvelope/.test(n) ||
      /\bichimoku\b/.test(n) ||
      /\bparabolic\b/.test(n) ||
      (/\bsar\b/.test(n) && !isOsc) ||
      /\bstandard deviation\b/.test(n) ||
      /\baverage directional\b/.test(n) ||
      /\badx\b/.test(n) ||
      /\bsupertrend\b/.test(n) ||
      /\bdonchian\b/.test(n) ||
      /\bkeltner\b/.test(n) ||
      /\bhull\b/.test(n) ||
      /\bzig ?zag\b/.test(n) ||
      /\blinear regression\b/.test(n) ||
      /\bmcginley\b/.test(n) ||
      (/\bmoving average\b/.test(n) && !/\bmacd\b/.test(n) && !/\bosillator\b/.test(n)) ||
      n === "ma" ||
      n === "ema" ||
      n === "sma" ||
      n === "wma" ||
      n === "vwap" ||
      /\btrend\b/.test(n));

  if (isWilliams) tabs.push("billWilliams");
  if (isVolume) tabs.push("volumes");
  if (isOsc) tabs.push("oscillators");
  if (isTrend) tabs.push("trend");
  return tabs;
}

function compareNames(a, b) {
  return a.localeCompare(b, undefined, { sensitivity: "base" });
}

function hexDigits(color) {
  return String(color || "").replace("#", "").slice(0, 6).toLowerCase();
}

function colorFromHex(value) {
  const digits = hexDigits(value);
  if (digits.length === 3) return `#${digits[0]}${digits[0]}${digits[1]}${digits[1]}${digits[2]}${digits[2]}`;
  if (digits.length === 6) return `#${digits}`;
  return "#2196f3";
}

function sourceLabel(value, titles) {
  if (titles && titles[value]) return titles[value];
  const map = {
    open: "Open",
    high: "High",
    low: "Low",
    close: "Close",
    hl2: "(H+L)/2",
    hlc3: "(H+L+C)/3",
    ohlc4: "(O+H+L+C)/4",
    hlcc4: "(H+L+C+C)/4",
  };
  return map[value] || value;
}

function StudyOptionsForm({ meta, form, setForm, t }) {
  const setInput = (id, value) => {
    setForm((prev) => ({ ...prev, inputs: { ...prev.inputs, [id]: value } }));
  };
  const setPlot = (id, patch) => {
    setForm((prev) => ({
      ...prev,
      plots: { ...prev.plots, [id]: { ...prev.plots[id], ...patch } },
    }));
  };

  return (
    <div className="mt5-ind-opt">
      {meta.inputs.map((inp) => {
        const value = form.inputs[inp.id];
        if (inp.type === "bool") {
          return (
            <label key={inp.id} className="mt5-ind-opt__checkrow">
              <input
                type="checkbox"
                className="mt5-ind-opt__check"
                checked={Boolean(value)}
                onChange={(e) => setInput(inp.id, e.target.checked)}
              />
              <span>{inp.name}</span>
            </label>
          );
        }
        if (inp.type === "source" || (Array.isArray(inp.options) && inp.options.length)) {
          const options = inp.options || ["open", "high", "low", "close", "hl2", "hlc3", "ohlc4"];
          return (
            <div key={inp.id} className="mt5-ind-opt__field">
              <span className="mt5-ind-opt__label">{inp.name}</span>
              <select
                className="mt5-ind-opt__select"
                value={value ?? ""}
                onChange={(e) => setInput(inp.id, e.target.value)}
              >
                {options.map((opt) => (
                  <option key={String(opt)} value={opt}>
                    {sourceLabel(opt, inp.optionsTitles)}
                  </option>
                ))}
              </select>
            </div>
          );
        }
        const numeric = inp.type === "integer" || inp.type === "float" || inp.type === "price";
        return (
          <div key={inp.id} className="mt5-ind-opt__field">
            <span className="mt5-ind-opt__label">{inp.name}</span>
            <input
              className="mt5-ind-opt__text"
              type={numeric ? "number" : "text"}
              inputMode={numeric ? "decimal" : "text"}
              min={inp.min}
              max={inp.max}
              step={inp.step || (inp.type === "integer" ? 1 : "any")}
              value={value ?? ""}
              onChange={(e) => {
                if (!numeric) {
                  setInput(inp.id, e.target.value);
                  return;
                }
                const next = e.target.value;
                setInput(inp.id, next === "" ? "" : Number(next));
              }}
            />
          </div>
        );
      })}

      {meta.plots.map((plot) => {
        const row = form.plots[plot.id] || plot;
        return (
          <div key={plot.id} className="mt5-ind-opt__line">
            <label className="mt5-ind-opt__line-name">
              <input
                type="checkbox"
                className="mt5-ind-opt__check"
                checked={row.visible !== false}
                onChange={(e) => setPlot(plot.id, { visible: e.target.checked })}
              />
              <span>{plot.title}</span>
            </label>
            <div className="mt5-ind-opt__swatch">
              <input
                type="color"
                className="mt5-ind-opt__color"
                value={colorFromHex(row.color)}
                aria-label={`${plot.title} color`}
                onChange={(e) => setPlot(plot.id, { color: e.target.value })}
              />
              <input
                className="mt5-ind-opt__hex"
                type="text"
                spellCheck={false}
                value={hexDigits(row.color)}
                onChange={(e) => setPlot(plot.id, { color: colorFromHex(e.target.value) })}
              />
            </div>
            <input
              className="mt5-ind-opt__width"
              type="range"
              min={1}
              max={4}
              step={1}
              value={Number(row.linewidth) || 1}
              aria-label={`${plot.title} width`}
              onChange={(e) => setPlot(plot.id, { linewidth: Number(e.target.value) })}
            />
            <span className="mt5-ind-opt__width-val">{Number(row.linewidth) || 1}</span>
          </div>
        );
      })}

      <label className="mt5-ind-opt__checkrow">
        <input
          type="checkbox"
          className="mt5-ind-opt__check"
          checked={form.allTimeframes !== false}
          onChange={(e) => setForm((prev) => ({ ...prev, allTimeframes: e.target.checked }))}
        />
        <span>{t("showAllTimeframes")}</span>
      </label>
    </div>
  );
}

export default function SelectIndicatorDialog({ darkTheme = false, onClose }) {
  const { t } = useAppLanguage();
  const [studies, setStudies] = useState([]);
  const [tab, setTab] = useState("all");
  const [selected, setSelected] = useState("");
  const [meta, setMeta] = useState({ inputs: [], plots: [] });
  const [form, setForm] = useState({ inputs: {}, plots: {}, allTimeframes: true });
  const [searchOpen, setSearchOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [adding, setAdding] = useState(false);
  const searchRef = useRef(null);
  const listRef = useRef(null);

  const refreshList = useCallback(() => {
    const next = getStudiesList().slice().sort(compareNames);
    setStudies(next);
  }, []);

  useEffect(() => {
    refreshList();
    return subscribeChartWidget(() => refreshList());
  }, [refreshList]);

  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  useEffect(() => {
    if (searchOpen) searchRef.current?.focus();
  }, [searchOpen]);

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    return studies.filter((name) => {
      if (q && !name.toLowerCase().includes(q)) return false;
      if (searchOpen || tab === "all") return true;
      return tabsForStudy(name).includes(tab);
    });
  }, [studies, tab, query, searchOpen]);

  useEffect(() => {
    if (selected && !visible.includes(selected)) setSelected("");
  }, [visible, selected]);

  useEffect(() => {
    if (!selected) {
      setMeta({ inputs: [], plots: [] });
      setForm({ inputs: {}, plots: {}, allTimeframes: true });
      return;
    }
    const nextMeta = getStudyMeta(selected);
    const inputs = {};
    nextMeta.inputs.forEach((inp) => {
      inputs[inp.id] = inp.defval;
    });
    const plots = {};
    nextMeta.plots.forEach((plot) => {
      plots[plot.id] = {
        color: plot.color,
        linewidth: plot.linewidth,
        visible: plot.visible,
      };
    });
    setMeta(nextMeta);
    setForm({ inputs, plots, allTimeframes: true });
  }, [selected]);

  const applyStudy = useCallback(
    async (name) => {
      const studyName = name || selected;
      if (!studyName || adding) return;
      setAdding(true);
      const inputs = {};
      Object.entries(form.inputs || {}).forEach(([id, value]) => {
        if (value === "" || value == null) return;
        inputs[id] = value;
      });
      const overrides = {};
      Object.entries(form.plots || {}).forEach(([id, plot]) => {
        if (!plot) return;
        overrides[`${id}.color`] = colorFromHex(plot.color);
        overrides[`${id}.linewidth`] = Number(plot.linewidth) || 1;
        overrides[`${id}.visible`] = plot.visible !== false;
        overrides[`${id}.display`] = plot.visible === false ? 0 : 15;
      });
      const ok = await addStudy(studyName, { inputs, overrides });
      setAdding(false);
      if (ok) onClose?.();
    },
    [selected, adding, form, onClose]
  );

  if (typeof document === "undefined") return null;

  const picked = Boolean(selected);

  return createPortal(
    <div
      className={`mt5-ind-overlay${darkTheme ? " mt5-ind-overlay--dark" : ""}`}
      role="presentation"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose?.();
      }}
    >
      <div
        className={`mt5-ind-dialog${picked ? " is-picked" : ""}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby="mt5-ind-title"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <header className="mt5-ind-dialog__head">
          <h2 id="mt5-ind-title" className="mt5-ind-dialog__title">
            {t("selectIndicator")}
          </h2>
          <button type="button" className="mt5-ind-dialog__close" onClick={onClose} aria-label={t("close")}>
            <svg viewBox="0 0 14 14" width="16" height="16" aria-hidden>
              <path
                d="M2.2 2.2l9.6 9.6M11.8 2.2L2.2 11.8"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.7"
                strokeLinecap="round"
              />
            </svg>
          </button>
        </header>

        <div className="mt5-ind-dialog__tabs">
          {searchOpen ? (
            <input
              ref={searchRef}
              className="mt5-ind-dialog__search"
              type="search"
              value={query}
              placeholder={t("search")}
              aria-label={t("search")}
              onChange={(e) => setQuery(e.target.value)}
            />
          ) : (
            <div className="mt5-ind-dialog__tablist" role="tablist" aria-label={t("selectIndicator")}>
              {TABS.map((item) => (
                <button
                  key={item.id}
                  type="button"
                  role="tab"
                  aria-selected={tab === item.id}
                  className={`mt5-ind-dialog__tab${tab === item.id ? " is-active" : ""}`}
                  onClick={() => {
                    setTab(item.id);
                    listRef.current?.scrollTo?.(0, 0);
                  }}
                >
                  {t(item.labelKey)}
                </button>
              ))}
            </div>
          )}
          <button
            type="button"
            className={`mt5-ind-dialog__search-btn${searchOpen ? " is-open" : ""}`}
            aria-label={t("search")}
            aria-pressed={searchOpen}
            onClick={() => {
              setSearchOpen((open) => {
                if (open) setQuery("");
                return !open;
              });
            }}
          >
            <svg viewBox="0 0 24 24" width="20" height="20" fill="none" aria-hidden>
              <circle cx="11" cy="11" r="6.2" stroke="currentColor" strokeWidth="1.8" />
              <path d="M15.8 15.8L20 20" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
            </svg>
          </button>
        </div>

        <div className="mt5-ind-dialog__body">
          <div ref={listRef} className="mt5-ind-dialog__list" role="listbox" aria-label={t("selectIndicator")}>
            {visible.map((name) => {
              const active = selected === name;
              return (
                <button
                  key={name}
                  type="button"
                  role="option"
                  aria-selected={active}
                className={`mt5-ind-dialog__row${active ? " is-selected" : ""}`}
                onClick={() => setSelected(name)}
              >
                  {name}
                </button>
              );
            })}
          </div>
          {picked ? (
            <div className="mt5-ind-dialog__opts">
              <StudyOptionsForm meta={meta} form={form} setForm={setForm} t={t} />
            </div>
          ) : null}
        </div>

        <footer className="mt5-ind-dialog__foot">
          <button type="button" className="mt5-ind-dialog__btn mt5-ind-dialog__btn--cancel" onClick={onClose}>
            {t("cancel")}
          </button>
          <button
            type="button"
            className="mt5-ind-dialog__btn mt5-ind-dialog__btn--add"
            disabled={!picked || adding}
            onClick={() => applyStudy(selected)}
          >
            {t("addIndicator")}
          </button>
        </footer>
      </div>
    </div>,
    document.body
  );
}
